/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/phone/setup/QaPhoneQuoteStep.tsx
 * FILE_ID: JOBHUB-QA-PHONE-QUOTE-STEP-001
 * PURPOSE: Quote cards plus the add-quote door. Any quote row can be ticked.
 */
import { type ReactNode } from 'react'
import { ExternalLink } from 'lucide-react'
import { StatusBadge } from '@quotech/ui'
import { cn, formatCurrency } from '@quotech/tenant-shared/lib/utils'
import type { QuoteRow } from '../../../../jobDetailTypes'
import { QuoteIngestErrorNotice } from '../../../QuoteIngestErrorNotice'

export function QaPhoneQuoteStep({
  quotes,
  selectedSheetIds,
  onToggleSheet,
  onRemoveQuote,
  onOpenQuote,
  addQuotes,
  onRetryImport,
  retryingImportId,
  retryImportError,
}: {
  quotes: QuoteRow[]
  selectedSheetIds: number[]
  onToggleSheet: (id: number) => void
  onRemoveQuote?: (id: number) => void
  onOpenQuote?: (q: QuoteRow) => void
  addQuotes?: ReactNode
  onRetryImport?: (quoteSheetId: number) => void
  retryingImportId?: number | null
  retryImportError?: { id: number; message: string } | null
}) {
  const requestToggle = (q: QuoteRow) => {
    if (retryingImportId === q.id) return
    onToggleSheet(q.id)
  }

  return (
    <div>
      {addQuotes}
      <ul className="mt-2 space-y-2">
        {quotes.filter((row) => !row.hidden_from_qa).map((q) => {
          const checked = selectedSheetIds.includes(q.id)
          const failed = q.ingest_error ?? null
          const total = Number.parseFloat(q.total_ex_gst)
          return (
            <li key={q.id} className="rounded-lg border border-border p-3">
              <div className="flex min-w-0 items-start gap-2">
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={retryingImportId === q.id}
                  onChange={() => requestToggle(q)}
                  aria-label={`Select quote ${q.quote_number}`}
                  className="mt-1 h-4 w-4"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{q.quote_number}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                    {!failed && (
                      <span>{Number.isFinite(total) ? formatCurrency(total) : '—'} ex GST</span>
                    )}
                    <StatusBadge status={q.status} />
                    {q.valid_until && <span>Valid {q.valid_until}</span>}
                    {q.deposit_pct ? <span>Deposit {q.deposit_pct}%</span> : null}
                    {q.is_primary && <span className="rounded-full bg-cat-4-muted px-1.5 text-[9px] text-cat-4">Contract</span>}
                  </div>
                </div>
                {onOpenQuote && (
                  <button
                    type="button"
                    onClick={() => onOpenQuote(q)}
                    className={cn('inline-flex min-h-[44px] items-center gap-1 px-2 text-xs text-primary')}
                  >
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden /> Open
                  </button>
                )}
                {onRemoveQuote && (
                  <button
                    type="button"
                    onClick={(event) => { event.stopPropagation(); onRemoveQuote(q.id) }}
                    className="inline-flex min-h-[44px] items-center gap-1 px-2 text-xs text-primary"
                  >
                    Remove
                  </button>
                )}
              </div>
              {failed && (
                <QuoteIngestErrorNotice
                  error={failed}
                  onRetry={() => onRetryImport?.(q.id)}
                  retrying={retryingImportId === q.id}
                  retryError={retryImportError?.id === q.id ? retryImportError.message : null}
                />
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
